/**
 * Write a plan by hand.
 *
 * The AI composer answers "I don't know what to do"; this answers "I know
 * exactly what to do and I want it typed in". It calls no model, costs
 * nothing, and works when the monthly AI budget is spent or the key is
 * missing — which is most of the reason it exists.
 *
 * Inputs follow the exercise's tracking mode, same as the log screen. A plank
 * asks for a hold, a row asks for distance or time, and neither is offered a
 * rep range it would have to invent an answer for.
 */

import { useEffect, useRef, useState } from 'react'

import { measuresOf, type Exercise, type Plan, type PlanItem } from '../db'
import { unitFor, useDistanceOverrides, type DistanceOverrides } from '../lib/distance'
import { api, ApiError } from '../api/client'
import { displayToMetres, durationToInput, metresToDisplay, parseDuration,
         type DistancePref, type Unit }
  from '../lib/units'
import { ExercisePicker } from './ExercisePicker'
import { DragList } from './DragList'
import { ItemRow, newItem, nextKey, type DraftItem } from './PlanItemFields'
import { ReadinessNote, useReadiness, verdictFor } from './Readiness'

type DraftDay = { key: string; id?: number; name: string; items: DraftItem[] }

/** An existing plan, turned back into something the form can edit. */
function toDraft(
  plan: Plan,
  exercises: Exercise[],
  opts: { unit: Unit; pref: DistancePref; overrides: DistanceOverrides },
): DraftDay[] {
  const byId = new Map(exercises.map((e) => [e.id, e]))
  return plan.days.map((day) => ({
    key: nextKey(),
    id: day.id,
    name: day.name,
    items: day.items.flatMap((item: PlanItem) => {
      // The exercise comes from the local cache rather than the plan payload,
      // because the form's inputs are driven by its tracking mode and the
      // nested copy carries only id and name.
      const exercise = item.exercise ? byId.get(item.exercise.id) : undefined
      if (!exercise) return []
      return [{
        ...newItem(exercise),
        sets: String(item.target_sets),
        reps: item.target_reps_low ? String(item.target_reps_low) : '',
        // Clock form, not raw seconds: the field masks what it holds, so a
        // stored 120 sitting there as "120" would jump to 1:20 on the first
        // keystroke of an unrelated edit.
        duration: durationToInput(item.target_duration_seconds),
        // Back into the unit the field is typed in — km or miles — or an
        // editing pass would silently rewrite 2 km as 2 miles.
        distance: item.target_distance_m
          ? String(metresToDisplay(item.target_distance_m,
                                   unitFor(exercise, opts)) ?? '') : '',
        rpe: item.target_rpe ? String(item.target_rpe) : '',
        rest: String(item.rest_seconds),
        notes: item.notes ?? '',
      }]
    }),
  }))
}

export function PlanBuilder({
  exercises,
  unit,
  distancePref,
  editing,
  onDone,
  onCancel,
}: {
  exercises: Exercise[]
  unit: Unit
  distancePref: DistancePref
  /** An existing plan to edit. Omitted, this writes a new one. */
  editing?: Plan
  onDone: (plan: Plan) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(editing?.name ?? '')
  const [weeks, setWeeks] = useState(String(editing?.weeks ?? 8))
  // Read once, synchronously, for the initial draft only — the form is a draft
  // from here on and re-resolving units under someone mid-edit would rewrite
  // numbers they are looking at.
  const overrides = useDistanceOverrides()
  const [days, setDays] = useState<DraftDay[]>(() =>
    editing
      ? toDraft(editing, exercises, { unit, pref: distancePref, overrides })
      : [{ key: nextKey(), name: 'Day 1', items: [] }],
  )
  const [pickingFor, setPickingFor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const card = useRef<HTMLDivElement | null>(null)

  // Editing opens this form above the plan you tapped Edit on, which is
  // usually well down the page — so without this the screen doesn't visibly
  // change and the button reads as broken.
  //
  // Two passes, because one isn't reliable here. Mounting the form pushes
  // everything below it down, and a smooth scroll started in the same frame
  // aims at a position the layout then moves out from under it, landing short.
  // The follow-up corrects that, and skips the jump entirely when the first
  // pass already arrived — so in the common case nothing visibly happens twice.
  useEffect(() => {
    if (!editing) return

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const scroll = (behavior: ScrollBehavior) =>
      card.current?.scrollIntoView({ behavior, block: 'start' })

    const frame = requestAnimationFrame(() => scroll(reduced ? 'auto' : 'smooth'))

    // After the smooth animation would have finished.
    const settle = setTimeout(() => {
      const top = card.current?.getBoundingClientRect().top ?? 0
      if (Math.abs(top) > 24) scroll('auto')
    }, 450)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(settle)
    }
  }, [editing?.id])

  const patchDay = (key: string, patch: Partial<DraftDay>) =>
    setDays((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)))

  // Advisory only, and only when there's a connection. A note that can't load
  // must never stop a plan being written — see Readiness.tsx.
  const readiness = useReadiness()

  /**
   * Swap a gated movement for the version that can actually be trained.
   *
   * The target's own numbers are kept — sets, rest, notes, and reps where the
   * substitute is also counted in reps. Both sides of every pair here are, but
   * `newItem` decides that from the tracking mode rather than this assuming it,
   * so a future gate whose substitute is timed doesn't silently inherit "8".
   */
  const swapItem = (dayKey: string, itemKey: string, substitute: string) => {
    const replacement = exercises.find(
      (e) => e.name.toLowerCase() === substitute.toLowerCase() && !e.archived_at)
    if (!replacement) return
    setDays((ds) =>
      ds.map((d) =>
        d.key !== dayKey ? d : {
          ...d,
          items: d.items.map((i) => {
            if (i.key !== itemKey) return i
            const fresh = newItem(replacement)
            return {
              ...fresh,
              sets: i.sets,
              reps: fresh.reps ? i.reps || fresh.reps : '',
              rpe: i.rpe,
              rest: i.rest,
              notes: i.notes,
            }
          }),
        }))
  }

  const patchItem = (dayKey: string, itemKey: string, patch: Partial<DraftItem>) =>
    setDays((ds) =>
      ds.map((d) =>
        d.key === dayKey
          ? {
              ...d,
              items: d.items.map((i) => (i.key === itemKey ? { ...i, ...patch } : i)),
            }
          : d,
      ),
    )

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        weeks: parseInt(weeks, 10) || 8,
        days: days.map((d) => ({
          // Kept so the server can update the day in place. A day that loses
          // its id would be deleted and recreated, and every session and
          // scheduled slot pointing at it would be orphaned.
          id: d.id,
          name: d.name.trim(),
          items: d.items.map((i) => {
            const ms = measuresOf(i.exercise)
            return {
              exercise_id: i.exercise.id,
              target_sets: parseInt(i.sets, 10) || 3,
              target_reps: ms.includes('reps') ? parseInt(i.reps, 10) || null : null,
              target_duration_seconds: ms.includes('time') ? parseDuration(i.duration) : null,
              target_distance_m: ms.includes('distance')
                ? displayToMetres(parseFloat(i.distance),
                                  unitFor(i.exercise, { unit, pref: distancePref, overrides }))
                : null,
              target_rpe: i.rpe ? parseFloat(i.rpe) : null,
              rest_seconds: parseInt(i.rest, 10) || 120,
              notes: i.notes.trim() || null,
            }
          }),
        })),
      }
      const res = editing
        ? await api<{ plan: Plan }>(`/plans/${editing.id}`,
                                    { method: 'PUT', body: payload })
        : await api<{ plan: Plan }>('/plans', { body: payload })
      onDone(res.plan)
    } catch (err) {
      // The server's message names the offending exercise, which is far more
      // useful than anything this component could work out for itself.
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't save the plan. Check your connection and try again.",
      )
    } finally {
      setBusy(false)
    }
  }

  const totalItems = days.reduce((n, d) => n + d.items.length, 0)
  const canSave = name.trim().length > 0 && totalItems > 0 && !busy

  return (
    // scrollMarginTop keeps the heading clear of the top edge rather than
    // flush against it, and leaves room for the offline banner when it's up.
    <div className="card" ref={card} style={{ scrollMarginTop: '.75rem' }}>
      <h2 style={{ marginTop: 0 }}>{editing ? 'Edit plan' : 'Build your own'}</h2>

      <label htmlFor="plan-name">Plan name</label>
      <input
        id="plan-name"
        value={name}
        placeholder="Upper/Lower, 4 days"
        onChange={(e) => setName(e.target.value)}
      />

      <label htmlFor="plan-weeks">Weeks</label>
      <input
        id="plan-weeks"
        type="number"
        inputMode="numeric"
        value={weeks}
        onChange={(e) => setWeeks(e.target.value)}
      />

      {days.map((day, dayIndex) => (
        <div
          key={day.key}
          style={{
            marginTop: '1rem',
            padding: '.6rem .7rem',
            background: 'var(--surface-2)',
            borderRadius: 'var(--radius)',
          }}
        >
          <div className="row">
            <input
              value={day.name}
              aria-label={`Day ${dayIndex + 1} name`}
              onChange={(e) => patchDay(day.key, { name: e.target.value })}
              style={{ flex: 1, marginRight: '.5rem' }}
            />
            {days.length > 1 && (
              <button
                className="secondary"
                style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.85rem' }}
                onClick={() => setDays((ds) => ds.filter((d) => d.key !== day.key))}
              >
                Remove
              </button>
            )}
          </div>

          {/* Drag the grip to reorder. Order is the plan's order: the server
              assigns order_index from the position in the payload, so moving a
              row here and saving is the whole change — no new endpoint, and no
              per-item index to keep in step.

              handleOnly because these rows are made of text inputs, and itemStyle
              because DragList's rows are list items whose default padding and
              rule would draw a border through the middle of a card. */}
          <DragList
            items={day.items}
            idOf={(i) => i.key}
            handleOnly
            itemStyle={{ padding: 0, borderBottom: 'none' }}
            onReorder={(next) => patchDay(day.key, { items: next })}
            renderItem={(item) => (
              <ItemRow
                item={item}
                unit={unit}
                distancePref={distancePref}
                onChange={(patch) => patchItem(day.key, item.key, patch)}
                onRemove={() =>
                  patchDay(day.key, { items: day.items.filter((i) => i.key !== item.key) })
                }
              />
            )}
          />

          {/* Under the rows rather than inside one: a note attached to an item
              would sit between two text inputs and inside a drag handle's row,
              and it is about the choice of exercise, not about its numbers. */}
          {day.items.map((item) => {
            const verdict = verdictFor(readiness, item.exercise.name)
            if (!verdict) return null
            return (
              <ReadinessNote
                key={`${item.key}-note`}
                movement={verdict}
                unit={unit}
                onSwap={
                  // Only offer the swap if the substitute is in the library on
                  // this device. A button that silently does nothing is worse
                  // than no button.
                  verdict.use_instead
                    && exercises.some((e) =>
                      e.name.toLowerCase() === verdict.use_instead?.toLowerCase())
                    ? (sub) => swapItem(day.key, item.key, sub)
                    : undefined
                }
              />
            )
          })}

          {pickingFor === day.key ? (
            <div style={{ marginTop: '.5rem' }}>
              <ExercisePicker
                exercises={exercises}
                onPick={(id) => {
                  const exercise = exercises.find((e) => e.id === id)
                  if (exercise) {
                    patchDay(day.key, { items: [...day.items, newItem(exercise)] })
                  }
                  setPickingFor(null)
                }}
                onCancel={() => setPickingFor(null)}
              />
            </div>
          ) : (
            <button
              className="secondary"
              style={{ width: '100%' }}
              onClick={() => setPickingFor(day.key)}
            >
              Add exercise
            </button>
          )}
        </div>
      ))}

      <button
        className="secondary"
        style={{ width: '100%', marginTop: '.75rem' }}
        disabled={days.length >= 7}
        onClick={() =>
          setDays((ds) => [...ds, { key: nextKey(), name: `Day ${ds.length + 1}`, items: [] }])
        }
      >
        Add day
      </button>

      {error && <div className="error" style={{ marginTop: '.75rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem' }}>
        <button className="secondary" style={{ flex: 1 }} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button style={{ flex: 2 }} disabled={!canSave} onClick={save}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Save plan'}
        </button>
      </div>
    </div>
  )
}
